/**
 * Narrative–CPT Mismatch Validation Gate
 * 
 * Detects when generated narrative text contains anatomy/procedure language
 * that contradicts the actual CPT codes, preventing "Frankenstein" documents
 * from reaching payers (e.g., knee CPT with shoulder narrative).
 */

interface MismatchResult {
  valid: boolean;
  mismatches: string[];
}

/** Body-region keywords keyed by CPT family prefix */
const CPT_ANATOMY_MAP: Record<string, { region: string; contradictoryTerms: string[] }> = {
  // Knee
  '274': { region: 'knee', contradictoryTerms: ['shoulder', 'supraspinatus', 'rotator cuff', 'infraspinatus', 'subscapularis', 'glenohumeral', 'acromioplasty', 'labrum', 'glenoid', 'humeral head', 'biceps tendon anchor', 'hallux', 'metatarsal', 'hammertoe', 'plantar'] },
  '292': { region: 'knee (arthroscopic)', contradictoryTerms: ['shoulder', 'supraspinatus', 'rotator cuff', 'glenohumeral', 'acromioplasty', 'humeral head', 'hallux', 'metatarsal', 'plantar'] },
  // Shoulder
  '298': { region: 'shoulder', contradictoryTerms: ['total knee', 'tibial', 'femoral component', 'patellar', 'meniscus', 'cruciate ligament', 'knee arthroplasty', 'hallux', 'metatarsal', 'plantar', 'hammertoe'] },
  '237': { region: 'shoulder (arthroplasty)', contradictoryTerms: ['tibial', 'femoral component', 'patellar', 'meniscus', 'total knee arthroplasty', 'hallux', 'metatarsal'] },
  // Hip
  '271': { region: 'hip', contradictoryTerms: ['shoulder', 'supraspinatus', 'rotator cuff', 'glenohumeral', 'meniscus', 'cruciate ligament', 'tibial plateau', 'hallux', 'metatarsal', 'plantar'] },
  '272': { region: 'hip', contradictoryTerms: ['shoulder', 'supraspinatus', 'rotator cuff', 'glenohumeral', 'meniscus', 'cruciate ligament', 'hallux', 'metatarsal'] },
  // Spine
  '228': { region: 'spine', contradictoryTerms: ['supraspinatus', 'rotator cuff', 'glenohumeral', 'meniscus', 'cruciate ligament', 'patellar', 'hallux', 'metatarsal', 'plantar'] },
  // Foot & Ankle (Podiatry / Ortho foot subspecialty)
  '280': { region: 'foot', contradictoryTerms: ['shoulder', 'rotator cuff', 'glenohumeral', 'knee arthroplasty', 'meniscus', 'cruciate', 'lumbar fusion'] },
  '281': { region: 'foot', contradictoryTerms: ['shoulder', 'rotator cuff', 'glenohumeral', 'knee arthroplasty', 'meniscus', 'cruciate', 'lumbar fusion'] },
  '282': { region: 'foot/ankle', contradictoryTerms: ['shoulder', 'rotator cuff', 'glenohumeral', 'knee arthroplasty', 'meniscus', 'cruciate', 'lumbar fusion'] },
  '283': { region: 'foot/ankle', contradictoryTerms: ['shoulder', 'rotator cuff', 'glenohumeral', 'knee arthroplasty', 'meniscus', 'cruciate', 'lumbar fusion'] },
};

/** T-modifiers (digit modifiers) are valid only on foot/ankle CPTs (28xxx, Q4xxx skin substitutes on foot, 15xxx wound). */
const T_MODIFIERS = new Set(['TA', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9']);
const FOOT_CPT_PREFIXES = new Set(['280', '281', '282', '283', '150', 'Q41', 'Q42']);

/**
 * Returns true if the given CPT code is in the foot/ankle/digit family
 * eligible for T-modifier appendage.
 */
function isFootCpt(cpt: string): boolean {
  if (!cpt) return false;
  if (FOOT_CPT_PREFIXES.has(cpt.substring(0, 3))) return true;
  if (cpt.startsWith('Q4')) return true;
  return false;
}

/**
 * Validates that narrative text does not contain anatomy terms that
 * contradict the provided CPT codes.
 * 
 * @param cptCodes - Array of CPT code strings (e.g., ['27447', '27485'])
 * @param narrativeText - The generated narrative / operative note text
 * @returns MismatchResult with valid=false if contradictions detected
 */
export function validateNarrativeCptAlignment(
  cptCodes: string[],
  narrativeText: string,
): MismatchResult {
  const mismatches: string[] = [];
  const lowerText = narrativeText.toLowerCase();

  for (const cpt of cptCodes) {
    // Match the first 3 digits of CPT against known families
    const prefix = cpt.substring(0, 3);
    const rule = CPT_ANATOMY_MAP[prefix];
    if (!rule) continue;

    for (const term of rule.contradictoryTerms) {
      if (lowerText.includes(term.toLowerCase())) {
        mismatches.push(
          `CPT ${cpt} (${rule.region}) conflicts with narrative containing "${term}"`
        );
      }
    }
  }

  return {
    valid: mismatches.length === 0,
    mismatches,
  };
}

/**
 * Modifier–CPT alignment gate.
 *
 * Hard-blocks the application of podiatry T-modifiers (TA, T1–T9) on
 * non-foot/ankle CPT codes. Prevents the cross-pollination scenario
 * (e.g., a knee CPT 27447 receiving a "T1" digit modifier).
 */
export function validateModifierCptAlignment(
  cptCode: string,
  modifier: string,
): { valid: boolean; reason?: string } {
  const upper = (modifier || '').toUpperCase();
  if (!T_MODIFIERS.has(upper)) return { valid: true };
  if (!isFootCpt(cptCode)) {
    return {
      valid: false,
      reason: `Digit modifier ${upper} cannot be applied to CPT ${cptCode} — T-modifiers are foot/ankle only.`,
    };
  }
  return { valid: true };
}
